/**
 * ProcessSection Organism Component
 * Process steps with insights and optional graphic
 */

import React from "react";
import { storyblokEditable, StoryblokComponent } from "@/lib/blocks";
import { Container } from "@/components/atoms/Container";
import { Heading } from "@/components/atoms/Heading";
import { Text } from "@/components/atoms/Text";
import { Image } from "@/components/atoms/Image";
import type { ProcessSectionBlok } from "@/types";

export interface ProcessSectionProps {
  blok: ProcessSectionBlok;
}

export const ProcessSection: React.FC<ProcessSectionProps> = ({ blok }) => {
  const {
    section_title,
    section_subtitle,
    steps,
    insights_title,
    insight_items,
    graphic,
    background_color,
  } = blok;

  return (
    <section
      {...storyblokEditable(blok)}
      id={(blok as {anchor?: string}).anchor || undefined}
      className="scroll-mt-24 py-12 md:py-16 lg:py-20 xl:py-24"
      style={{ backgroundColor: background_color }}
    >
      <Container>
        {/* Section Header */}
        <div className="flex flex-col items-start gap-4 md:gap-6 mb-4 md:mb-6">
          <Heading
            level="h2"
            className="text-black dark:text-primary"
          >
            {section_title}
          </Heading>
          {section_subtitle && (
            <Text size="base" className="text-gray-700 max-w-2xl md:max-w-3xl">
              {section_subtitle}
            </Text>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16">
          {/* Process Steps */}
          <div className="flex flex-col gap-6 md:gap-8">
            {steps &&
              steps.map((step, index) => (
                <div key={step._uid} className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white font-bold flex-shrink-0">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <StoryblokComponent blok={step} />
                  </div>
                </div>
              ))}
          </div>

          {/* Insights or Graphic */}
          <div className="flex flex-col gap-6 md:gap-8">
            {graphic && (
              <Image
                src={graphic.filename}
                alt={graphic.alt || section_title}
                className="w-full h-auto rounded-lg"
              />
            )}

            {insight_items && insight_items.length > 0 && (
              <div className="flex flex-col gap-4 md:gap-6">
                <Heading level="h3" className="text-xl md:text-2xl font-bold">
                  {insights_title || "Key Insights"}
                </Heading>
                <div className="flex flex-col gap-3 md:gap-4">
                  {insight_items.map((item) => (
                    <StoryblokComponent key={item._uid} blok={item} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </Container>
    </section>
  );
};

export default ProcessSection;
